/**
 * useOpenClawAgentInfo — Fetch agent name, model, sync status from runner
 * Reads ~/.openclaw/openclaw.json via exec-direct, auto-refetches every 30s
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { isDockerMode } from '../lib/openclawPaths';

export interface OpenClawAgentInfo {
  name: string | null;
  description: string | null;
  model: string | null;
  lastSynced: Date | null;
  filesCount: number;
  status: 'synced' | 'loading' | 'offline';
}

const REFETCH_INTERVAL = 30_000; // 30s

export function useOpenClawAgentInfo(runnerId: string, isOnline: boolean, openclawPath?: string) {
  const [info, setInfo] = useState<OpenClawAgentInfo>({
    name: null, description: null, model: null, lastSynced: null, filesCount: 0, status: 'loading',
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchInfo = useCallback(async () => {
    if (!isOnline || !runnerId) {
      setInfo(prev => ({ ...prev, status: 'offline' }));
      return;
    }

    try {
      const base = isDockerMode(openclawPath) ? '/data/.openclaw' : (openclawPath?.replace('data:', '') || '~/.openclaw');
      const cmd = `cat ${base}/openclaw.json 2>/dev/null || echo "NO_FILE"; echo "---BRAIN---"; ls -1 ${base}/workspace/*.md ${base}/workspace/*.yaml 2>/dev/null | wc -l`;

      const { runCommandDirect } = await import('../lib/runCommand.js');
      const output = await runCommandDirect(api, runnerId, cmd);

      const parts = output.split('---BRAIN---');
      const jsonPart = (parts[0] || '').trim();
      const countPart = (parts[1] || '0').trim();

      if (!jsonPart || jsonPart === 'NO_FILE') {
        setInfo({ name: null, description: null, model: null, lastSynced: new Date(), filesCount: 0, status: 'synced' });
        return;
      }

      // Clean output (remove bash noise)
      const lines = jsonPart.split('\n').filter(l => {
        const t = l.trim();
        return t && !t.startsWith('dircolors:') && !t.startsWith('bash:') && !t.startsWith('mesg:') && !t.startsWith('warning:');
      });
      const cleanJson = lines.join('\n');

      let name: string | null = null;
      let description: string | null = null;
      let model: string | null = null;

      try {
        const parsed = JSON.parse(cleanJson);
        // openclaw.json structure: { agents: [{ name, model, ... }] } or { name, model }
        if (parsed.agents && Array.isArray(parsed.agents) && parsed.agents.length > 0) {
          name = parsed.agents[0].name || parsed.agents[0].label || null;
          description = parsed.agents[0].description || null;
          model = parsed.agents[0].model || null;
        } else if (parsed.agents?.list && Array.isArray(parsed.agents.list) && parsed.agents.list.length > 0) {
          name = parsed.agents.list[0].name || null;
          description = parsed.agents.list[0].description || null;
          model = parsed.agents.list[0].model || null;
        } else {
          name = parsed.name || parsed.agent_name || null;
          description = parsed.description || null;
          model = parsed.model || null;
        }
      } catch {
        // Try extracting name/model with regex as fallback
        const nameMatch = cleanJson.match(/"name"\s*:\s*"([^"]+)"/);
        const modelMatch = cleanJson.match(/"model"\s*:\s*"([^"]+)"/);
        if (nameMatch) name = nameMatch[1];
        if (modelMatch) model = modelMatch[1];
      }

      const filesCount = parseInt(countPart, 10) || 0;

      setInfo({ name, description, model, lastSynced: new Date(), filesCount, status: 'synced' });
    } catch (err) {
      console.error('[useOpenClawAgentInfo] fetch failed:', err);
      setInfo(prev => ({ ...prev, status: prev.lastSynced ? 'offline' : 'offline' }));
    }
  }, [runnerId, isOnline, openclawPath]);

  useEffect(() => {
    fetchInfo();
    intervalRef.current = setInterval(fetchInfo, REFETCH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchInfo]);

  return { ...info, refetch: fetchInfo };
}
